import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Mail, Send, ChevronDown, ChevronRight } from "lucide-react";
import { errorMessage, EMAIL_KIND_LABELS, LIFECYCLE_EMAIL_KINDS } from "@atlas/shared";
import {
  fetchAdminEmailSummary,
  fetchAdminEmailLog,
  sendAdminTestEmail,
  type AdminEmailLogEntry,
  type AdminEmailLogQuery,
} from "../../api/email";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

const PAGE_SIZE = 50;

const inputStyle: React.CSSProperties = {
  height: 34,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

export function kindLabel(kind: string): string {
  return EMAIL_KIND_LABELS[kind] ?? kind;
}

export function statusBadge(status: string) {
  const variant =
    status === "sent" ? "success" : status === "failed" ? "danger" : status === "pending" ? "info" : "neutral";
  return <Badge variant={variant}>{status}</Badge>;
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Tile({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
        padding: "14px 16px",
        minWidth: 0,
      }}
    >
      <div className="eyebrow" style={{ fontSize: 10 }}>{label}</div>
      <div className="tnum" style={{ fontFamily: "var(--font-heading)", fontSize: 28, color: "var(--text-primary)", marginTop: 4 }}>
        {value.toLocaleString()}
      </div>
      {sub && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Summary ─────────────────────────────────────────────────────────────────

function SummarySection() {
  const summaryQuery = useQuery({
    queryKey: ["admin/emails/summary"],
    queryFn: fetchAdminEmailSummary,
  });
  const s = summaryQuery.data?.summary;

  if (summaryQuery.isLoading) return <Skel height={120} />;
  if (!s) {
    return <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>Couldn&#39;t load the summary.</p>;
  }

  const kinds = Array.from(new Set(s.byKind.map((r) => r.kind)));
  const cell = (kind: string, status: string, window: "last7d" | "last30d") =>
    s.byKind.find((r) => r.kind === kind && r.status === status)?.[window] ?? 0;

  return (
    <>
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12 }}>
        <Tile label="Sent 7d" value={s.sent7d} />
        <Tile label="Sent 30d" value={s.sent30d} />
        <Tile label="Failed 30d" value={s.failed30d} sub={`${s.failed7d} in the last 7 days`} />
        <Tile label="Skipped 30d" value={s.skipped30d} sub="Preference, threshold or nothing to say" />
        <Tile label="Pending summaries" value={s.pendingImportMarkers} sub="Import batches still landing" />
      </div>

      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: 20 }}>
        <AtlasPanel title="By kind (last 30 days)">
          {kinds.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Nothing sent in the last 30 days.</p>
          ) : (
            <div className="r-scroll-x" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>Kind</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Sent 7d</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Sent 30d</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Failed 30d</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Skipped 30d</th>
                  </tr>
                </thead>
                <tbody>
                  {kinds.map((kind) => (
                    <tr key={kind} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                      <td style={{ padding: "8px", color: "var(--text-primary)" }}>{kindLabel(kind)}</td>
                      <td className="tnum" style={{ padding: "8px", textAlign: "right" }}>{cell(kind, "sent", "last7d")}</td>
                      <td className="tnum" style={{ padding: "8px", textAlign: "right" }}>{cell(kind, "sent", "last30d")}</td>
                      <td className="tnum" style={{ padding: "8px", textAlign: "right", color: cell(kind, "failed", "last30d") > 0 ? "var(--status-danger)" : undefined }}>
                        {cell(kind, "failed", "last30d")}
                      </td>
                      <td className="tnum" style={{ padding: "8px", textAlign: "right", color: "var(--text-muted)" }}>{cell(kind, "skipped", "last30d")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AtlasPanel>

        <AtlasPanel title="Opt-outs">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(["activity", "digest", "product"] as const).map((category) => {
              const o = s.optOuts[category];
              return (
                <div key={category}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: "var(--text-primary)", textTransform: "capitalize" }}>{category}</span>
                    <span className="tnum" style={{ color: "var(--text-muted)" }}>
                      {o.count.toLocaleString()} · {o.pct}%
                    </span>
                  </div>
                  <div style={{ height: 6, background: "var(--surface-sunken)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, o.pct)}%`, height: "100%", background: "var(--status-warning, var(--accent))" }} />
                  </div>
                </div>
              );
            })}
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
              Of {s.optOuts.totalUsers.toLocaleString()} accounts. Account emails have no opt-out.
            </p>
          </div>
        </AtlasPanel>
      </div>
    </>
  );
}

// ─── Test send ───────────────────────────────────────────────────────────────

function TestSendPanel() {
  const { user } = useAuth();
  const [kind, setKind] = React.useState<string>(LIFECYCLE_EMAIL_KINDS[0] ?? "import_summary");
  const mutation = useMutation({ mutationFn: (k: string) => sendAdminTestEmail(k) });

  return (
    <AtlasPanel title="Send me a test">
      <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ ...inputStyle, minWidth: 200 }}>
          {LIFECYCLE_EMAIL_KINDS.map((k) => (
            <option key={k} value={k}>{kindLabel(k)}</option>
          ))}
        </select>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Send size={13} />}
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(kind)}
        >
          {mutation.isPending ? "Queuing…" : `Send to ${user?.email ?? "me"}`}
        </Button>
        {mutation.isSuccess && (
          <span style={{ fontSize: 12, color: mutation.data.queued ? "var(--spruce-300)" : "var(--text-muted)" }}>
            {mutation.data.queued ? "Queued. Rendered with your own data; bypasses preferences and dedupe." : "Email queue not configured in this environment."}
          </span>
        )}
        {mutation.isError && (
          <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
            {errorMessage(mutation.error, "Failed")}
          </span>
        )}
      </div>
    </AtlasPanel>
  );
}

// ─── Send log ────────────────────────────────────────────────────────────────

function LogRow({ entry }: { entry: AdminEmailLogEntry }) {
  const [open, setOpen] = React.useState(false);
  const hasDetail = !!entry.error || Object.keys(entry.meta).length > 0;
  return (
    <>
      <tr style={{ borderTop: "1px solid var(--border-subtle)", cursor: hasDetail ? "pointer" : "default" }} onClick={() => hasDetail && setOpen((o) => !o)}>
        <td style={{ padding: "8px", whiteSpace: "nowrap", color: "var(--text-muted)" }} className="tnum">{formatWhen(entry.createdAt)}</td>
        <td style={{ padding: "8px", color: "var(--text-primary)" }}>{kindLabel(entry.kind)}</td>
        <td style={{ padding: "8px" }}>
          <div style={{ color: "var(--text-primary)" }}>{entry.toEmail}</div>
          {entry.userName && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{entry.userName}</div>}
        </td>
        <td style={{ padding: "8px", color: "var(--text-secondary)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={entry.subject}>
          {entry.subject || <span style={{ color: "var(--text-muted)" }}>-</span>}
        </td>
        <td style={{ padding: "8px" }}>{statusBadge(entry.status)}</td>
        <td style={{ padding: "8px", width: 24, color: "var(--text-muted)" }}>
          {hasDetail ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ padding: "0 8px 10px" }}>
            {entry.error && (
              <div style={{ fontSize: 12, color: "var(--status-danger)", marginBottom: 6 }}>{entry.error}</div>
            )}
            <pre style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)" }}>
              {JSON.stringify({ ...entry.meta, dedupeKey: entry.dedupeKey ?? undefined }, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function LogSection() {
  const [filters, setFilters] = React.useState<AdminEmailLogQuery>({});
  const [q, setQ] = React.useState("");
  const [page, setPage] = React.useState(0);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, q }));
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const logQuery = useQuery({
    queryKey: ["admin/emails/log", filters, page],
    queryFn: () => fetchAdminEmailLog({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
  });
  const entries = logQuery.data?.entries ?? [];
  const total = logQuery.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const set = (patch: AdminEmailLogQuery) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  return (
    <AtlasPanel
      title="Send log"
      action={<span style={{ fontSize: 12, color: "var(--text-muted)" }} className="tnum">{total.toLocaleString()} entries</span>}
    >
      <div className="r-wrap" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <input
          type="search"
          placeholder="Search email or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ ...inputStyle, flex: "1 1 220px" }}
        />
        <select value={filters.kind ?? ""} onChange={(e) => set({ kind: e.target.value || undefined })} style={inputStyle}>
          <option value="">All kinds</option>
          {Object.keys(EMAIL_KIND_LABELS).map((k) => (
            <option key={k} value={k}>{kindLabel(k)}</option>
          ))}
        </select>
        <select value={filters.status ?? ""} onChange={(e) => set({ status: e.target.value || undefined })} style={inputStyle}>
          <option value="">All statuses</option>
          {["sent", "failed", "skipped", "pending"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input type="date" value={filters.from ?? ""} onChange={(e) => set({ from: e.target.value || undefined })} style={inputStyle} title="From" />
        <input type="date" value={filters.to ?? ""} onChange={(e) => set({ to: e.target.value || undefined })} style={inputStyle} title="To" />
      </div>

      {logQuery.isLoading ? (
        <Skel height={160} />
      ) : entries.length === 0 ? (
        <EmptyState icon={<Mail size={22} />} title="No emails match" hint="Nothing has been logged for these filters yet." />
      ) : (
        <div className="r-scroll-x" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
                <th style={{ padding: "6px 8px", fontWeight: 500 }}>When</th>
                <th style={{ padding: "6px 8px", fontWeight: 500 }}>Kind</th>
                <th style={{ padding: "6px 8px", fontWeight: 500 }}>To</th>
                <th style={{ padding: "6px 8px", fontWeight: 500 }}>Subject</th>
                <th style={{ padding: "6px 8px", fontWeight: 500 }}>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <LogRow key={e.id} entry={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>
          <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="tnum">Page {page + 1} of {pages}</span>
          <Button size="sm" variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </AtlasPanel>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminEmailsPage() {
  const { user } = useAuth();
  if (!user?.isAdmin) return <AccessDenied />;

  return (
    <AdminShell title="Emails">
      <SummarySection />
      <TestSendPanel />
      <LogSection />
    </AdminShell>
  );
}
